import { useState, useRef } from "react";
import { useAuth, getAuthHeaders } from "@/contexts/AuthContext";
import { Camera } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EditableImage({ src, alt, className, onReplace, children }) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  if (!user) {
    return children || <img src={src} alt={alt || ""} className={className} />;
  }

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error(`File too large (${(file.size/1024/1024).toFixed(1)} MB). Max 50 MB.`);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/upload/image`, formData, {
        headers: getAuthHeaders()
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      if (onReplace) {
        await onReplace(url);
      }
      toast.success("Image replaced!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed. Try JPG/PNG under 50 MB.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="relative group/edit">
      {children || <img src={src} alt={alt || ""} className={className} />}
      <div
        className="absolute inset-0 bg-black/0 group-hover/edit:bg-black/40 transition-all duration-300 flex items-center justify-center cursor-pointer z-10"
        onClick={() => fileRef.current?.click()}
        data-testid="image-replace-overlay"
      >
        <div className="opacity-0 group-hover/edit:opacity-100 transition-opacity duration-300 flex flex-col items-center gap-2">
          {uploading ? (
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Camera className="w-8 h-8 text-white drop-shadow-lg" />
              <span className="text-white text-xs uppercase tracking-[0.15em] font-semibold drop-shadow-lg bg-black/50 px-3 py-1 rounded">
                Replace Image
              </span>
            </>
          )}
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleUpload}
        className="hidden"
      />
    </div>
  );
}
